import React, { useEffect, useState } from 'react';
import { JBActionBar, JBAlert, JBButton } from '@jbhi-fi/lanyard-ui';
import Select from '@jbhi-fi/lanyard-ui/components/Select';
import { BIRTH_MONTH_DESCRIPTION, MONTH_ARRAY } from '../../../customers/constants';
import { useVerifyLayout } from '../../../customers/hooks/useVerifyLayout/useVerifyLayout';
import { formAction, useTrackingContext, trackingContext } from '@jbhi-fi/components/src/Tracking';
import { useMobileVerifyContext } from '../../../customers/context/MobileVerifyContext';
import { useSetMobile } from '../../../customers/hooks/useSetMobile/useSetMobile';
import { MobileVerifyResponseType } from '../../../customers/hooks/useMobileVerify';
import { CustomerClient } from '../../../customers/api/customer/CustomerClient';
import { useCustomerApi } from '../../../customers/hooks/customerApi';
import TextField, { InputType } from '@jbhi-fi/lanyard-ui/components/TextField';
import { styles } from './AddMobileAndBirthday.css';
import { Icon } from '@jbhi-fi/lanyard-ui/components/Icon';

const MONTH_SELECT_OPTIONS = MONTH_ARRAY.map((month, i) => ({
  label: month,
  value: `${i + 1}`,
}));
const MOBILE_FIELD_DESCRIPTION =
  'In-store receipts sent to you will be automatically saved to your account. We’ll send a 4-digit verification code in the next step.';

export type AddMobileAndBirthdayProps = {
  heading: string;
  content?: string;
  showCancel?: boolean;
  showLogo?: boolean;
  hasBorder?: boolean;
  variant?: 'full' | 'small';
  handleRedirect: (data: MobileVerifyResponseType) => void;
};

export const AddMobileAndBirthday: React.FC<AddMobileAndBirthdayProps> = ({
  heading,
  content,
  showCancel = false,
  showLogo = true,
  hasBorder = !Boolean(showCancel),
  variant = 'full',
  handleRedirect,
}) => {
  const [birthdayMonth, setBirthdayMonth] = useState<number | null>(null);
  const [isBirthdaySaving, setIsBirthdaySaving] = useState<boolean>(false);
  const [showBirthdayError, setShowBirthdayError] = useState<boolean>(false);

  const { data: mobileVerifyData } = useMobileVerifyContext();
  const { currentMobileNumber } = mobileVerifyData;
  const { tracker } = useTrackingContext();
  const { themeContext, setThemeContext } = useVerifyLayout();
  const { receiptPhone, receiptCode } = themeContext ?? {};
  const {
    saveError,
    submitting,
    handleSave,
    handleChange: handleNumberChange,
    mobileInput,
    isMobileValid,
  } = useSetMobile({
    handleRedirect,
    tracker,
    receiptPhone,
    receiptCode,
    suppressTracking: true,
  });
  const { customer, setCustomer } = useCustomerApi();
  const skipButtonLabel = variant === 'small' ? 'No thanks' : 'Skip for now';

  useEffect(() => {
    document.title = 'Link in-store purchases - Account - JB Hi-Fi';
    tracker.group = currentMobileNumber ? 'Add birthday month' : 'verify mobile number';
    tracker.trackFormEvent({ action: formAction.stepView });
    tracker.trackFormEvent({ action: formAction.start });
  }, []);

  const handleCancel = () => {
    tracker.trackClick({ label: 'Cancel' });
    window.location.href = 'account#/manage-account';
  };

  const skipSetBirthday = async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    event.preventDefault();
    tracker.trackClick({ label: skipButtonLabel });
    setThemeContext(themeContext => ({ ...themeContext, skipBirthdayPrompt: true, isPerksFinished: true }));
  };

  const onBirthdayChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setShowBirthdayError(false);
    setBirthdayMonth(parseInt(event.target.value));
  };

  const onSubmit = async (
    event:
      | React.MouseEvent<HTMLButtonElement>
      | React.FormEvent<HTMLFormElement>
      | React.KeyboardEvent<HTMLInputElement>
  ) => {
    event.preventDefault();
    setIsBirthdaySaving(true);

    if (!birthdayMonth) {
      if (showCancel || currentMobileNumber) {
        setIsBirthdaySaving(false);
        setShowBirthdayError(true);
        tracker.trackError({ errorMsg: 'Please select a birthday month' });
        return;
      }
      setThemeContext(themeContext => ({ ...themeContext, skipBirthdayPrompt: true, isPerksFinished: true }));
    }
    tracker.trackClick({ label: 'verify mobile number' });
    birthdayMonth && (!currentMobileNumber || receiptPhone)
      ? tracker.trackFormEvent({ label: 'Birth Month and Phone', action: formAction.fieldComplete })
      : birthdayMonth && currentMobileNumber && !receiptPhone
      ? tracker.trackFormEvent({ label: 'Birth Month', action: formAction.fieldComplete })
      : !birthdayMonth
      ? tracker.trackFormEvent({ label: 'Phone', action: formAction.fieldComplete })
      : null;

    const { ok, error, response } = currentMobileNumber
      ? { ok: true, response: mobileVerifyData, error: null }
      : await handleSave();

    if (!ok && error?.heading) {
      tracker.trackError({ errorMsg: error.heading, contextOverride: trackingContext.form });
    }

    if (birthdayMonth && ok) {
      const { data, error } = await CustomerClient.postBirthMonth({
        customerId: customer?.id ?? mobileVerifyData.mongoCustomerId,
        birthMonth: birthdayMonth,
      });
      if (currentMobileNumber && !receiptPhone) {
        tracker.trackFormEvent({ action: formAction.complete });
      }
      if (data) setCustomer(data);

      setThemeContext(themeContext => ({
        ...themeContext,
        isPerksFinished: true,
        ...(Boolean(error) ? { skipBirthdayPrompt: true } : { birthMonthSelected: true }),
      }));
    }
    if (ok && response) handleRedirect(response);
  };

  return (
    <>
      <div className="alerts">
        {saveError ? (
          <JBAlert showIcon type="warning" heading={saveError.heading} content={saveError.content ?? ''} />
        ) : null}
      </div>
      <div>
        <div className={styles.container({ hasBorder: hasBorder, variant: variant })}>
          {showLogo ? (
            <div className={styles.iconContainer({ variant: variant })}>
              <Icon name="PerksBirthdayMascotIllustrated" width={99} height={93} />
            </div>
          ) : null}
          {variant === 'small' ? (
            <h3 className={styles.heading3}>{heading}</h3>
          ) : (
            <>
              <h2 className={styles.heading2}>{heading}</h2>
              {content ? <div className={styles.contentContainer}>{content}</div> : null}
            </>
          )}

          <Select
            id="birthday_month"
            label={
              showCancel || (currentMobileNumber && !receiptPhone) ? 'Birthday month' : 'Birthday month (optional)'
            }
            name="birthday_month"
            onChange={e => onBirthdayChange(e)}
            errorMessage="Please select a birthday month."
            hasError={showBirthdayError}
            options={MONTH_SELECT_OPTIONS}
            value={`${birthdayMonth ?? ''}`}
            className={styles.birthdayInput({ hasBorder: hasBorder })}
          />

          <small className={styles.fieldDescription}>
            {!currentMobileNumber && !receiptPhone ? BIRTH_MONTH_DESCRIPTION : null}
          </small>

          {!currentMobileNumber && !receiptPhone ? (
            <div className={styles.phoneInputContainer}>
              <TextField
                id="mobile-input"
                name="mobile-input"
                label="Mobile phone"
                value={mobileInput}
                onChange={handleNumberChange}
                hasError={!isMobileValid && Boolean(mobileInput)}
                errorMsg="Mobile phone number invalid, give it another go."
                type={InputType.tel}
                onKeyDown={e => {
                  e.key === 'Enter' ? onSubmit(e) : null;
                }}
                maxLength={10}
              />
              <p className={styles.mobileInfoText}>{MOBILE_FIELD_DESCRIPTION}</p>
            </div>
          ) : null}

          {receiptPhone && !currentMobileNumber ? (
            <>
              <div className={styles.phoneNumberContainer}>
                <p className={styles.phoneNumberHeader}>
                  <strong>Mobile number</strong>
                </p>
                <p className={styles.phoneNumber}>{receiptPhone}</p>
                <p className={styles.phoneNumberDescription}>{MOBILE_FIELD_DESCRIPTION}</p>
              </div>
              <div className={styles.ctaContainer}>
                <JBButton
                  type="primary"
                  fullWidth
                  label={submitting ? 'Submitting...' : 'Verify phone number'}
                  onClick={e => !submitting && onSubmit(e)}
                />
              </div>
            </>
          ) : !showCancel && !currentMobileNumber ? (
            <div className={styles.ctaContainer}>
              <JBButton
                type="primary"
                fullWidth
                label={submitting ? 'Submitting...' : 'Verify phone number'}
                onClick={e => !submitting && onSubmit(e)}
              />
            </div>
          ) : null}

          {!receiptPhone && showCancel ? (
            <JBActionBar
              childrenLeft={<JBButton type="link" fullWidth label="Cancel" onClick={handleCancel} />}
              childrenRight={
                <JBButton
                  type="primary"
                  label={isBirthdaySaving ? 'Saving...' : 'Save'}
                  onClick={e => !isBirthdaySaving && onSubmit(e)}
                />
              }
            />
          ) : currentMobileNumber ? (
            <div className={styles.ctaContainer}>
              <JBButton
                type="primary"
                fullWidth
                label={submitting ? 'Submitting...' : 'Save'}
                onClick={e => !submitting && onSubmit(e)}
              />
              <div className={styles.skipButton({ variant: variant })}>
                <JBButton type="link" fullWidth label={skipButtonLabel} onClick={e => skipSetBirthday(e)} />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};
